import { MARKET_PRICE_SETTING_OPTIONS } from "constants/marketPriceSetting";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadMarketPrices } from "store/queries/marketPrices";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { MarketPriceSetting } from "types/marketPrice";

import {
  buildDisplayNames,
  MAX_MARKET_PRICES_FILE_SIZE_LABEL,
  runSequentialUploads,
  UploadStatus,
  validateMarketPricesFile,
} from "./utils/marketPricesUploadQueue";

const PageContainer = styled.div`
  padding: ${sizer(4)};
  max-width: 800px;
  margin: 0 auto;
`;

const FormField = styled.div`
  margin-bottom: ${sizer(3)};
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: ${sizer(1)};
  color: #495057;
`;

const RequiredStar = styled.span`
  color: #e74c3c;
  margin-left: ${sizer(0.5)};
`;

const HelperText = styled.small`
  color: #6c757d;
  display: block;
  margin-top: ${sizer(1)};
`;

const FileList = styled.div`
  margin-bottom: ${sizer(2)};
`;

const FileRow = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: ${sizer(1)} ${sizer(2)};
  margin-bottom: ${sizer(1)};
`;

const FileError = styled.small`
  color: #e74c3c;
  display: block;
  margin-top: ${sizer(1)};
  white-space: pre-wrap;
  word-break: break-word;
`;

const Summary = styled.div`
  background-color: #fff5f5;
  border: 1px solid #f5c2c7;
  border-radius: 4px;
  padding: ${sizer(2)};
  margin-top: ${sizer(3)};
  word-break: break-word;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${sizer(2)};
  justify-content: flex-end;
  margin-top: ${sizer(4)};
`;

interface IQueuedFile {
  id: number;
  file: File;
  status: UploadStatus;
  skippedCount?: number;
  error?: string;
}

const STATUS_LABELS: Record<UploadStatus, string> = {
  pending: "Waiting",
  uploading: "Uploading...",
  success: "Uploaded",
  failed: "Failed",
};

const STATUS_SEVERITIES: Record<
  UploadStatus,
  "info" | "warning" | "success" | "danger"
> = {
  pending: "info",
  uploading: "warning",
  success: "success",
  failed: "danger",
};

const formatMegabytes = (bytes: number) =>
  `${(bytes / 1_000_000).toFixed(1)} MB`;

const isSameFile = (a: File, b: File) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const skippedSentence = (skipped: number) =>
  `${skipped} ${skipped === 1 ? "entry was" : "entries were"} skipped because ${
    skipped === 1 ? "it already exists" : "they already exist"
  }.`;

export function MarketPricesUpload() {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [uploadMarketPrices] = useUploadMarketPrices();

  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [queuedFiles, setQueuedFiles] = useState<IQueuedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // Set synchronously, so a second click before the next render cannot start a second batch.
  const isUploadingRef = useRef(false);
  const nextFileId = useRef(0);
  // PrimeReact's basic FileUpload hides its input after a selection; remounting it resets it.
  const [fileInputKey, setFileInputKey] = useState(0);
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  // CLE-2939: no default — the SuperAdmin must say which setting the file's prices are for.
  const [defaultSetting, setDefaultSetting] =
    useState<MarketPriceSetting | null>(null);
  const [isComparisonSetting, setIsComparisonSetting] = useState(false);

  const { data: providers } = useGetAllProvidersQuery();

  // Get provider options for multiselect
  const providerOptions =
    providers?.data?.map((provider: any) => ({
      label: provider.name,
      value: provider.id,
    })) || [];

  const remainingFiles = queuedFiles.filter(
    (queued) => queued.status !== "success"
  );
  const failedFiles = queuedFiles.filter(
    (queued) => queued.status === "failed"
  );
  const uploadingIndex = queuedFiles.findIndex(
    (queued) => queued.status === "uploading"
  );
  const doneCount = queuedFiles.filter(
    (queued) => queued.status === "success"
  ).length;

  // PrimeReact's basic mode does not enforce maxFileSize (onSelect gets every chosen file), so
  // the type and size checks are ours.
  const handleFileSelect = (event: any) => {
    const files: File[] = Array.from(event.files ?? []);
    const accepted: IQueuedFile[] = [];
    const rejected: string[] = [];
    files.forEach((file) => {
      const error = validateMarketPricesFile(file);
      if (error) {
        rejected.push(error);
        return;
      }
      const alreadyQueued = [...queuedFiles, ...accepted].some((queued) =>
        isSameFile(queued.file, file)
      );
      if (!alreadyQueued) {
        nextFileId.current += 1;
        accepted.push({ id: nextFileId.current, file, status: "pending" });
      }
    });
    if (accepted.length) {
      setQueuedFiles((current) => [...current, ...accepted]);
    }
    // Shown until the next selection: a toast would be gone before a dozen files are checked.
    setRejectedFiles(rejected);
    setFileInputKey((key) => key + 1);
  };

  const removeFile = (id: number) =>
    setQueuedFiles((current) => current.filter((queued) => queued.id !== id));

  const showSingleFileError = (errorMessage: string) => {
    // For longer error messages, show them in a more readable format
    if (errorMessage.length > 100) {
      toast.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail: errorMessage,
        life: 10000, // Show for 10 seconds for longer messages
        sticky: errorMessage.length > 200, // Make it sticky for very long messages
      });
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
      });
    }
  };

  const handleSubmit = async () => {
    if (isUploadingRef.current) return;
    if (!displayName.trim()) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Display name is required",
      });
      return;
    }

    if (!remainingFiles.length) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a CSV file",
      });
      return;
    }

    if (!selectedProviderIds.length) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select at least one provider",
      });
      return;
    }

    if (!defaultSetting) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a setting",
      });
      return;
    }

    isUploadingRef.current = true;
    setIsUploading(true);

    // Names come from the whole selection, so a retried file keeps the name it had the first time.
    const names = buildDisplayNames(
      displayName,
      queuedFiles.map((queued) => queued.file)
    );
    const nameById = new Map(
      queuedFiles.map((queued, index) => [queued.id, names[index]])
    );
    const isSingleFile = queuedFiles.length === 1;
    const toUpload = remainingFiles;

    // Every file gets the same form choices. isComparisonSetting is safe to send on each: it sets
    // Provider.marketPriceComparisonSetting to the same value every time.
    const results = await runSequentialUploads(
      toUpload,
      (queued) =>
        uploadMarketPrices({
          file: queued.file,
          displayName: nameById.get(queued.id) as string,
          description: description.trim() || undefined,
          providerIds: selectedProviderIds,
          defaultSetting,
          isComparisonSetting,
        }).unwrap(),
      (index, outcome) => {
        const { id } = toUpload[index];
        setQueuedFiles((current) =>
          current.map((queued) =>
            queued.id === id ? { ...queued, ...outcome } : queued
          )
        );
      }
    );

    isUploadingRef.current = false;
    setIsUploading(false);

    const failures = results.filter((result) => result.status === "failed");
    if (failures.length) {
      if (isSingleFile) {
        showSingleFileError(failures[0].error ?? "");
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Some files failed",
          detail: `${failures.length} of ${queuedFiles.length} files failed to upload. See the list below.`,
        });
      }
      return;
    }

    const skipped = results.reduce(
      (total, result) => total + (result.skippedCount ?? 0),
      0
    );
    const successDetail = isSingleFile
      ? "Market prices uploaded successfully"
      : `${queuedFiles.length} market price files uploaded successfully`;
    toast.current?.show({
      severity: "success",
      summary: "Success",
      detail:
        skipped > 0
          ? `${successDetail}. ${skippedSentence(skipped)}`
          : successDetail,
    });
    navigate(`/payer-rules?tab=4`); // Navigate to Market Prices tab
  };

  const handleCancel = () => {
    navigate(`/payer-rules?tab=4`);
  };

  let submitLabel = "Upload";
  if (isUploading) {
    submitLabel = `Uploading ${uploadingIndex + 1} of ${queuedFiles.length}...`;
  } else if (
    failedFiles.length &&
    failedFiles.length === remainingFiles.length
  ) {
    submitLabel = `Retry failed (${failedFiles.length})`;
  } else if (remainingFiles.length > 1) {
    submitLabel = `Upload ${remainingFiles.length} files`;
  }

  return (
    <PageContainer>
      <Toast ref={toast} />
      <Card title="Upload Market Prices">
        <FormField className="mb-4">
          <Label>
            Display Name <RequiredStar>*</RequiredStar>
          </Label>
          <InputText
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Q4 2024 Market Rates"
            className="w-full"
            disabled={isUploading}
          />
          <HelperText>
            With several files, each dataset is named &quot;Display Name — file
            name&quot;.
          </HelperText>
        </FormField>

        <FormField className="mb-4">
          <Label>Description</Label>
          <InputTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description of this market price dataset"
            rows={3}
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField className="mb-4">
          <Label>
            Provider(s) <RequiredStar>*</RequiredStar>
          </Label>
          <MultiSelect
            value={selectedProviderIds}
            options={providerOptions}
            onChange={(e) => setSelectedProviderIds(e.value)}
            placeholder="Select Provider(s)"
            className="w-full"
            filter
            display="chip"
            showClear
            disabled={isUploading}
          />
          <HelperText>
            Select the providers this market price data applies to. The same
            rates will be applied to all selected providers.
          </HelperText>
        </FormField>

        <FormField className="mb-4">
          <Label>
            Setting <RequiredStar>*</RequiredStar>
          </Label>
          <Dropdown
            value={defaultSetting}
            options={MARKET_PRICE_SETTING_OPTIONS}
            onChange={(e) => setDefaultSetting(e.value ?? null)}
            placeholder="Select a setting"
            className="w-full"
            disabled={isUploading}
          />
          <HelperText>
            Where these competitor prices apply. Used for every CSV row whose
            optional setting column is blank; rows with a setting of their own
            (HOSPITAL, ASC, PHYSICIAN_OFFICE or PHYSICIAN_FACILITY) keep it.
          </HelperText>
          <div className="flex align-items-center gap-2 mt-3">
            <Checkbox
              inputId="market-prices-upload-comparison-setting"
              checked={isComparisonSetting}
              onChange={(e) => setIsComparisonSetting(e.checked ?? false)}
              disabled={isUploading}
            />
            <label htmlFor="market-prices-upload-comparison-setting">
              This is the provider&apos;s own setting (used for comparison)
            </label>
          </div>
          <HelperText>
            Sets the default tab in Smart Insights and what the comparison and
            AI insights use.
          </HelperText>
        </FormField>

        <FormField>
          <Label>
            CSV File(s) <RequiredStar>*</RequiredStar>
          </Label>
          {queuedFiles.length > 0 && (
            <FileList>
              {queuedFiles.map((queued) => (
                <FileRow
                  key={queued.id}
                  data-testid="market-prices-upload-file"
                >
                  <div className="flex align-items-center justify-content-between gap-2">
                    <div>
                      <strong>{queued.file.name}</strong>{" "}
                      <span className="text-color-secondary">
                        ({formatMegabytes(queued.file.size)})
                      </span>
                    </div>
                    <div className="flex align-items-center gap-2">
                      <Tag
                        value={STATUS_LABELS[queued.status]}
                        severity={STATUS_SEVERITIES[queued.status]}
                        icon={
                          queued.status === "uploading"
                            ? "pi pi-spin pi-spinner"
                            : undefined
                        }
                      />
                      {queued.status !== "success" && (
                        <Button
                          icon="pi pi-times"
                          className="p-button-text p-button-danger p-button-sm"
                          aria-label={`Remove ${queued.file.name}`}
                          onClick={() => removeFile(queued.id)}
                          disabled={isUploading}
                        />
                      )}
                    </div>
                  </div>
                  {queued.status === "success" && !!queued.skippedCount && (
                    <HelperText>
                      {queued.skippedCount} skipped (already exist)
                    </HelperText>
                  )}
                  {queued.status === "failed" && (
                    <FileError>{queued.error}</FileError>
                  )}
                </FileRow>
              ))}
            </FileList>
          )}
          <FileUpload
            key={fileInputKey}
            mode="basic"
            name="file"
            accept=".csv"
            multiple
            chooseLabel={
              queuedFiles.length ? "Add CSV Files" : "Choose CSV Files"
            }
            className="w-full"
            onSelect={handleFileSelect}
            disabled={isUploading}
          />
          {rejectedFiles.length > 0 && (
            <div data-testid="market-prices-upload-rejected">
              {rejectedFiles.map((message) => (
                <FileError key={message}>Not added: {message}</FileError>
              ))}
            </div>
          )}
          <HelperText className="mt-2">
            Maximum file size: {MAX_MARKET_PRICES_FILE_SIZE_LABEL} per file. You
            can select several files; they are uploaded one at a time, each as
            its own dataset. The CSV must include columns for entity_name, Payer
            Name, CPT Code, and rate.
          </HelperText>
        </FormField>

        {isUploading && queuedFiles.length > 1 && (
          <ProgressBar
            value={Math.round((doneCount / queuedFiles.length) * 100)}
            style={{ height: "6px" }}
          />
        )}
        {isUploading && queuedFiles.length <= 1 && (
          <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
        )}

        {!isUploading && failedFiles.length > 0 && (
          <Summary data-testid="market-prices-upload-summary">
            <div>
              <strong>
                {doneCount} of {queuedFiles.length} files uploaded.
              </strong>{" "}
              These failed and are still selected, so you can retry them:
            </div>
            <ul className="mt-2 mb-0">
              {failedFiles.map((queued) => (
                <li key={queued.id}>
                  {queued.file.name}: {queued.error}
                </li>
              ))}
            </ul>
          </Summary>
        )}

        <ButtonGroup>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={handleCancel}
            disabled={isUploading}
          />
          <Button
            data-testid="market-prices-upload-submit"
            label={submitLabel}
            icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
            onClick={handleSubmit}
            disabled={
              isUploading ||
              !remainingFiles.length ||
              !displayName.trim() ||
              !defaultSetting
            }
          />
        </ButtonGroup>
      </Card>
    </PageContainer>
  );
}

export default MarketPricesUpload;
